import { useState, type ReactNode } from 'react';
import { Button, Card, CardHeader, CardTitle, Field, Input, Textarea } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AccountNote } from '../types';
import './insights.css';
import './account.css';

export interface NoteDraft {
  kind: 'note' | 'next_step';
  body: string;
  dueOn: string | null;
}

export interface NotesPanelProps {
  notes: readonly AccountNote[];
  /** False for a viewer: the notes show, the form does not. */
  canWrite: boolean;
  busy: boolean;
  /** Which kind the form opens on (the header's "Add note" or "Next step"). */
  kind: 'note' | 'next_step';
  onKind: (kind: 'note' | 'next_step') => void;
  onAdd: (draft: NoteDraft) => void;
  onDone: (id: string) => void;
  /** Open the form even with no notes yet (the header's buttons, or a word in the one-line form). */
  expanded?: boolean;
}

function KindSwitch({ kind, onKind }: Pick<NotesPanelProps, 'kind' | 'onKind'>): ReactNode {
  return (
    <div className="ad-switch" role="group" aria-label="Kind">
      <button type="button" aria-pressed={kind === 'note'} onClick={() => onKind('note')}>
        Note
      </button>
      <button type="button" aria-pressed={kind === 'next_step'} onClick={() => onKind('next_step')}>
        Next step
      </button>
    </div>
  );
}

type FormProps = Pick<NotesPanelProps, 'kind' | 'onKind' | 'busy' | 'onAdd'>;

function Fields(x: {
  kind: NoteDraft['kind'];
  body: string;
  due: string;
  setBody: (v: string) => void;
  setDue: (v: string) => void;
}) {
  return (
    <>
      <Field label={x.kind === 'note' ? 'Note' : 'Next step'}>
        {({ id }) => (
          <Textarea
            id={id}
            value={x.body}
            maxLength={2000}
            onChange={(e) => x.setBody(e.target.value)}
          />
        )}
      </Field>
      {x.kind === 'next_step' ? (
        <Field label="Due">
          {({ id }) => (
            <Input id={id} type="date" value={x.due} onChange={(e) => x.setDue(e.target.value)} />
          )}
        </Field>
      ) : null}
    </>
  );
}

function NoteForm({ kind, onKind, busy, onAdd }: FormProps): ReactNode {
  const [body, setBody] = useState('');
  const [due, setDue] = useState('');
  const send = () => {
    onAdd({ kind, body: body.trim(), dueOn: kind === 'next_step' && due ? due : null });
    setBody('');
    setDue('');
  };
  return (
    <form
      className="ad-note-form"
      onSubmit={(e) => (e.preventDefault(), body.trim() ? send() : undefined)}
    >
      <KindSwitch kind={kind} onKind={onKind} />
      <Fields kind={kind} body={body} due={due} setBody={setBody} setDue={setDue} />
      <Button type="submit" variant="primary" size="sm" disabled={!body.trim()} loading={busy}>
        Save
      </Button>
    </form>
  );
}

function OpenSteps({
  steps,
  canWrite,
  onDone,
}: {
  steps: readonly AccountNote[];
  canWrite: boolean;
  onDone: (id: string) => void;
}) {
  const f = useFormat();
  return (
    <>
      {steps.map((n) => (
        <p key={n.id} className="ad-next-step">
          <strong>Next step{n.dueOn ? `, due ${f.date(`${n.dueOn}T12:00:00Z`)}` : ''}:</strong>{' '}
          {n.body} <span className="ad-small">({n.author})</span>{' '}
          {canWrite ? (
            <Button size="sm" variant="ghost" onClick={() => onDone(n.id)}>
              Done
            </Button>
          ) : null}
        </p>
      ))}
    </>
  );
}

/** No notes yet: one line, not an empty form (round 3); either word opens the form on its kind. */
function NotesLine(props: NotesPanelProps): ReactNode {
  return (
    <p className="ad-notes-line" id="ad-notes">
      <strong>Notes</strong> <span className="ad-small">none yet; staff only.</span>
      {props.canWrite ? (
        <>
          {' '}
          <Button size="sm" variant="ghost" onClick={() => props.onKind('note')}>
            Add a note
          </Button>
          <Button size="sm" variant="ghost" onClick={() => props.onKind('next_step')}>
            Set a next step
          </Button>
        </>
      ) : null}
    </p>
  );
}

function NoteList({ notes }: { notes: readonly AccountNote[] }): ReactNode {
  const f = useFormat();
  const kind = (n: AccountNote) =>
    n.kind === 'next_step' ? ` · next step${n.doneAt ? `, done by ${n.doneBy ?? ''}` : ''}` : '';
  return (
    <ul className="ad-notes">
      {notes.length === 0 ? <li className="ad-small">No notes yet.</li> : null}
      {notes.map((n) => (
        <li key={n.id}>
          <span className="ad-small">
            {f.dateTime(n.createdAt)} · {n.author}
            {kind(n)}
          </span>
          <p>{n.body}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Staff notes and the next step on the account (round 1, sales S6): the open next step first with its
 * due day, then the notes, newest first, each with who and when. Append-only: a note is never edited;
 * a next step is marked done. Never shown to the company.
 */
export function NotesPanel(props: NotesPanelProps): ReactNode {
  const open = props.notes.filter((n) => n.kind === 'next_step' && !n.doneAt);
  if (props.notes.length === 0 && !props.expanded) return <NotesLine {...props} />;
  return (
    <Card className="ad-vol__card" id="ad-notes">
      <CardHeader>
        <CardTitle>Notes and next step</CardTitle>
        <span className="ad-small">Ademero staff only; the company never sees these</span>
      </CardHeader>
      <OpenSteps steps={open} canWrite={props.canWrite} onDone={props.onDone} />
      {props.canWrite ? (
        <NoteForm kind={props.kind} onKind={props.onKind} busy={props.busy} onAdd={props.onAdd} />
      ) : null}
      <NoteList notes={props.notes.filter((n) => !open.includes(n))} />
    </Card>
  );
}
